import { useEffect, memo, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Form, Formik } from 'formik'

import { Box, Button, makeStyles, TextField, Typography, Avatar, Chip } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import LinkIcon from '@material-ui/icons/Link'
import GifIcon from '@material-ui/icons/Gif'
import CloseIcon from '@material-ui/icons/Close'

import { GROUP, SpacedGroup, WhiteButton } from 'UI/Components'

import { StartAtInput } from './StartAtInput'

import {
    useBoolean,
    useCopySharedLink,
    useNotification,
    useCopyLinkedGif,
    usePlaybookShare,
    useShareableOptions,
    useAuth,
    logShareByEmail,
    useActivePlaybook
} from 'hooks'

import { formatTime, option, timeToSeconds, validateEmail } from 'modules'
import { PendingEmailType, PlaybookType, ShareableSpacesUserType } from 'app/types'

const useStyles = makeStyles({
    disabled: {
        backgroundColor: '#CB0000 !important',
        opacity: 0.4,
        color: '#FFFFFF !important'
    },
    endAdornment: {
        top: '-16px !important',
        right: '8px'
    },
    greyLine: {
        position: 'absolute',
        bottom: '-1px',
        left: 0,
        right: 0,
        background: '#fff',
        height: '5px',
        width: '100%'
    },
    redLine: {
        position: 'absolute',
        bottom: '-3px',
        left: 0,
        right: 0,
        background: '#cb0000',
        height: '1px',
        width: 'calc(100% + 20px)',
        transform: 'translateX(-10px)'
    },
    chip: {
        color: '#090C10',
        fontSize: 14,
        background: 'rgba(9, 12, 16, 0.08)',
        border: '1px solid rgba(9, 12, 16, 0.08)',
        borderRadius: 16,
        height: '24px !important'
    }
})

const generateStartValue = (time: number) => {
    const value = Math.floor(time)
    const data = formatTime(value).split(':')
    if (data.length < 3) data.unshift('00')

    return data.join(':')
}

type SubmitType = {
    options: Array<{
        isSelected: boolean
        email: string
        label: string
        value: string
        group: typeof GROUP.pendingEmails | typeof GROUP.teamMembers
    }>
}

type Props = {
    playbook: PlaybookType
    onClose: () => void
    onDone: (playbookId?: string) => void
    setLoading: (val: boolean) => void
}

const getEmailOption = (user: PendingEmailType) => ({
    ...option(user.email, user.email),
    isSelected: false,
    email: user.email,
    group: GROUP.pendingEmails,
    name: '',
    logo: ''
})

const getUserOption = (user: ShareableSpacesUserType) => ({
    ...option(user.uid, user.email),
    email: user.email,
    isSelected: false,
    group: GROUP.teamMembers,
    name: user.displayName,
    logo: user.logo
})

export const ShareTab = memo(({ playbook, onClose, onDone, setLoading }: Props) => {
    const classes = useStyles()

    const startAt = useBoolean()
    const focussed = useBoolean()

    const [input, setInput] = useState('')

    const { uid } = useAuth()
    const { activePlaybook } = useActivePlaybook()

    const { copySharedLink } = useCopySharedLink()
    const { copyLinkedGif } = useCopyLinkedGif()

    const { showErrorNotification, showSuccessNotification } = useNotification()
    const [startTime, setStartTime] = useState('00:00:00')

    const { currentTime } = useSelector(state => state.videoEditor)

    useEffect(() => {
        setStartTime(generateStartValue(currentTime))
    }, [currentTime])

    const { sharePlaybook, isSharing, ApprovalModal } = usePlaybookShare({
        onSuccess: data => {
            if (data?.code === 422) return

            onClose()
            onDone()
            showSuccessNotification(`Video shared successfully`)
        }
    })

    const $options = useShareableOptions({
        playbooksIds: [playbook.id],
        onError: onClose
    })

    const { data } = $options
    const optionsList = useMemo(
        () => [
            ...(data?.internalUsers?.map(getUserOption) || []),
            ...(data?.externalUsers?.map(getUserOption) || []),
            ...(data?.pendingEmails?.map(getEmailOption) || [])
        ],
        [data]
    )

    const onCopy = () => {
        let query = ''

        if (startAt.isTrue) {
            query += `&t=${timeToSeconds(startTime)}`
        }

        if (playbook.isPlaylist) {
            query += `&active=${activePlaybook}`
        }

        copySharedLink(
            playbook,
            true,
            {
                context: 'share-link'
            },
            query
        )
    }

    const loading = $options.isValidating || isSharing

    useEffect(() => {
        setLoading(loading)
    }, [loading, setLoading])

    const notEnteredEmailIsValid = input && validateEmail(input)

    return (
        <Box>
            <StartAtInput startAt={startAt} startTime={startTime} setStartTime={setStartTime} />

            <SpacedGroup width="100%" mt={2} spacing={3}>
                <WhiteButton
                    data-cy="share-dialog-copy-link"
                    startIcon={<LinkIcon />}
                    fullWidth
                    onClick={onCopy}
                >
                    COPY LINK
                </WhiteButton>

                <WhiteButton
                    data-cy="share-dialog-copy-gif"
                    startIcon={<GifIcon />}
                    fullWidth
                    onClick={() => copyLinkedGif(playbook)}
                    color="default"
                >
                    COPY GIF
                </WhiteButton>
            </SpacedGroup>
            <Box mt={4} />
            <Formik
                initialValues={{
                    options: optionsList.filter(it => it.isSelected)
                }}
                enableReinitialize={true}
                onSubmit={({ options }: SubmitType) => {
                    setInput('')
                    const userIds = options
                        .filter(({ group }) => group === GROUP.teamMembers)
                        .map(o => o.value)

                    const emails = options
                        .filter(({ group }) => group === GROUP.pendingEmails)
                        .map(o => o.value)

                    const includeInvalidEmail = emails.some(email => !validateEmail(email))

                    if (includeInvalidEmail) {
                        showErrorNotification('Some email is invalid')
                        return
                    } else {
                        sharePlaybook({
                            emails,
                            userIds,
                            playbooksIds: [playbook.id],
                            notificationsReceivers: 'membersAndSpaces',
                            shareMode: 'share'
                        }).then(() => {
                            logShareByEmail({
                                eventName: 'publish_playbook',
                                sharedByUid: uid,
                                sentTo: options.map(it => it?.email).filter(validateEmail),
                                playbookIds: [playbook.id]
                            })
                        })
                    }
                }}
            >
                {({ dirty, values, setFieldValue, handleSubmit }) => (
                    <Form>
                        <Autocomplete
                            multiple
                            value={values.options}
                            onChange={(e, value) => {
                                if (value.every(it => typeof it === 'object')) {
                                    setFieldValue('options', value)
                                } else {
                                    const modifiedValue = value.map(it => {
                                        if (typeof it === 'object') return it

                                        return {
                                            value: it.trim(),
                                            label: it,
                                            isSelected: false,
                                            group: GROUP.pendingEmails
                                        }
                                    })
                                    setFieldValue('options', modifiedValue)
                                }
                            }}
                            onFocus={focussed.setTrue}
                            onBlur={focussed.setFalse}
                            freeSolo
                            options={optionsList}
                            getOptionLabel={option => option.label}
                            disabled={$options.isValidating}
                            filterSelectedOptions
                            style={{
                                background: '#FFFFFF',
                                padding: `${values.options.length < 1 ? 0 : 8}px 8px 4px`,
                                marginTop: values.options.length < 1 ? '-10px' : 0,
                                borderRadius: '4px 4px 0 0',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                maxHeight: '200px'
                            }}
                            classes={{
                                endAdornment: classes.endAdornment
                            }}
                            renderTags={(params, getProps) => {
                                return params.map((it, index) => (
                                    <Chip
                                        label={it.name || it.email || it.value}
                                        key={it.value}
                                        classes={{ root: classes.chip }}
                                        deleteIcon={
                                            <CloseIcon
                                                fontSize="small"
                                                style={{
                                                    width: 15,
                                                    height: 15,
                                                    color: '#090C10',
                                                    marginRight: 5
                                                }}
                                            />
                                        }
                                        {...getProps({ index })}
                                    />
                                ))
                            }}
                            renderOption={params => (
                                <SpacedGroup>
                                    <Avatar src={params.logo} style={{ width: 40, height: 40 }} />
                                    <Box>
                                        <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                                            {params.name}
                                        </Typography>
                                        <Typography color="textSecondary" style={{ fontSize: 14 }}>
                                            {params.email}
                                        </Typography>
                                    </Box>
                                </SpacedGroup>
                            )}
                            renderInput={params => (
                                <Box position="relative">
                                    <TextField
                                        data-cy="share-dialog-send-email"
                                        {...params}
                                        onChange={e => {
                                            setInput(e.target.value)
                                        }}
                                        style={{
                                            textAlign: 'center',
                                            color: 'rgba(0, 0, 0, 0.54)'
                                        }}
                                        label={
                                            <Typography
                                                color={
                                                    focussed.isTrue ? 'inherit' : 'textSecondary'
                                                }
                                                style={{
                                                    marginTop: '-5px'
                                                }}
                                            >
                                                Send email
                                            </Typography>
                                        }
                                    />

                                    <Box className={classes.greyLine} />
                                    {focussed.isTrue && <Box className={classes.redLine} />}
                                </Box>
                            )}
                        />

                        <Box mt={2} display="flex" justifyContent="flex-end" width="100%">
                            <Button
                                data-cy="share-dialog-send-button"
                                disabled={(!dirty && !notEnteredEmailIsValid) || isSharing}
                                onClick={() => {
                                    if (notEnteredEmailIsValid) {
                                        // prevent adding duplications
                                        if (values.options.some(it => it.value !== input)) {
                                            // add email manually when entered field is email valid
                                            setFieldValue('options', [
                                                ...values.options,
                                                {
                                                    group: GROUP.pendingEmails,
                                                    isSelected: false,
                                                    label: input,
                                                    value: input
                                                }
                                            ])
                                        }
                                    }
                                    handleSubmit()
                                }}
                                color="primary"
                                className={
                                    (!dirty && !notEnteredEmailIsValid) || isSharing
                                        ? classes.disabled
                                        : ''
                                }
                            >
                                SEND
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>
            {ApprovalModal}
        </Box>
    )
})
